/**
 * Utilitaires frontend pour l'affichage des variantes matricielles
 *
 * Implémente la logique de fallback à 2 niveaux côté client pour :
 * - Images de variantes
 * - Descriptions de variantes
 * - Conditions de variantes
 *
 * Architecture :
 * - Niveau 2 (prioritaire) : Données de la valeur d'axe textuel sélectionnée
 * - Niveau 1 (fallback) : Données du type de bon cadeau
 *
 * @module variantDisplayHelper
 * @author Équipe Brydge
 * @since 2.5.0
 */

/**
 * Récupère les données d'affichage pour une carte de bon cadeau
 *
 * Détermine quelle image afficher selon la configuration du bon cadeau
 * et la présence de variantes textuelles avec images personnalisées.
 *
 * @param {Object} voucher - Le bon cadeau complet
 * @param {Object} [selectedCombination=null] - Combinaison sélectionnée (null en mode liste)
 * @return {Object} Données d'affichage
 * @return {string} returns.imagePath - URL de l'image à afficher
 * @return {boolean} returns.isCustomized - true si image personnalisée de variante
 */
export const getDisplayData = (voucher, selectedCombination = null) => {
  if (!voucher) {
    return {
      imagePath: null,
      isCustomized: false,
    };
  }

  if (voucher.amountType !== 'matrix') {
    return {
      imagePath: voucher.imagePath || null,
      isCustomized: false,
    };
  }

  return getMatrixDisplayData(voucher, selectedCombination);
};

/**
 * Identifie l'axe textuel principal pour l'affichage
 *
 * Stratégie de sélection :
 * 1. Premier axe textuel avec au moins une valeur ayant une image personnalisée
 * 2. Sinon, premier axe textuel par ordre de displayOrder
 * 3. Sinon, null
 *
 * @param {Object} voucher - Le bon cadeau avec variantConfig
 * @return {Object|null} L'axe textuel principal ou null
 */
export const getPrimaryTextAxis = (voucher) => {
  if (!voucher?.variantConfig?.axes) {
    return null;
  }

  const textAxes = voucher.variantConfig.axes.filter((axis) => axis.valueType === 'string');

  if (textAxes.length === 0) {
    return null;
  }

  const axisWithImages = textAxes.find((axis) =>
    axis.values?.some((value) => value.variantImagePath),
  );

  if (axisWithImages) {
    return axisWithImages;
  }

  const sortedTextAxes = [...textAxes].sort(
      (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0),
  );

  return sortedTextAxes[0];
};

/**
 * Récupère les données d'affichage pour un bon matriciel avec logique de fallback
 *
 * Applique le système de fallback à 2 niveaux pour les images :
 * - Niveau 2 (prioritaire) : Image personnalisée de la valeur d'axe textuel
 * - Niveau 1 (fallback) : Image du type de bon cadeau
 *
 * @private
 * @param {Object} voucher - Le bon cadeau matriciel
 * @param {Object} selectedCombination - Combinaison sélectionnée
 * @return {Object} Données d'affichage avec imagePath et isCustomized
 */
const getMatrixDisplayData = (voucher, selectedCombination) => {
  const primaryTextAxis = getPrimaryTextAxis(voucher);

  if (!primaryTextAxis) {
    return {
      imagePath: voucher.imagePath || null,
      isCustomized: false,
    };
  }

  const selectedValueKey = selectedCombination ? selectedCombination[primaryTextAxis.key] : null;

  if (!selectedValueKey) {
    const activeValues = primaryTextAxis.values?.filter((v) => v.isActive !== false) || [];

    if (activeValues.length > 0) {
      const sortedValues = [...activeValues].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
      const firstValue = sortedValues[0];

      return {
        imagePath: firstValue.variantImagePath || voucher.imagePath || null,
        isCustomized: !!firstValue.variantImagePath,
      };
    }

    return {
      imagePath: voucher.imagePath || null,
      isCustomized: false,
    };
  }

  const selectedValueObj = primaryTextAxis.values?.find((v) => v.value === selectedValueKey);

  if (!selectedValueObj) {
    return {
      imagePath: voucher.imagePath || null,
      isCustomized: false,
    };
  }

  const imagePath = selectedValueObj.variantImagePath || voucher.imagePath || null;

  return {
    imagePath,
    isCustomized: !!selectedValueObj.variantImagePath,
  };
};

/**
 * Récupère le contenu textuel (description + conditions) pour une variante
 *
 * Applique le fallback à 2 niveaux pour description et conditions :
 * - Niveau 2 (prioritaire) : Données de la valeur d'axe textuel sélectionnée
 * - Niveau 1 (fallback) : Données du type de bon cadeau
 *
 * @param {Object} voucher - Le bon cadeau complet
 * @param {Object} [selectedCombination=null] - Combinaison sélectionnée
 * @param {string} [language='fr'] - Langue pour les traductions
 * @return {Object} Données de contenu
 * @return {string} returns.description - Description à afficher (peut être vide)
 * @return {string} returns.conditions - Conditions à afficher (peut être vide)
 * @return {boolean} returns.isCustomized - true si contenu personnalisé de variante
 */
export const getContentData = (voucher, selectedCombination = null, language = 'fr') => {
  if (!voucher) {
    return {
      description: '',
      conditions: '',
      isCustomized: false,
    };
  }

  // Pour les types non-matriciels : utiliser directement les données du type
  if (voucher.amountType !== 'matrix') {
    const description = voucher.translations?.description?.[language] || voucher.description || '';
    const conditions = voucher.translations?.conditions?.[language] || voucher.conditions || '';

    return {
      description,
      conditions,
      isCustomized: false,
    };
  }

  // Pour les types matriciels : appliquer la logique de fallback
  return getMatrixContentData(voucher, selectedCombination, language);
};

/**
 * Récupère le contenu pour un bon matriciel avec logique de fallback
 *
 * @private
 * @param {Object} voucher - Le bon cadeau matriciel
 * @param {Object} selectedCombination - Combinaison sélectionnée
 * @param {string} language - Langue cible
 * @return {Object} Données de contenu avec description, conditions et isCustomized
 */
const getMatrixContentData = (voucher, selectedCombination, language) => {
  // Description et conditions du type (fallback niveau 1)
  const typeDescription = voucher.translations?.description?.[language] || voucher.description || '';
  const typeConditions = voucher.translations?.conditions?.[language] || voucher.conditions || '';

  const primaryTextAxis = getPrimaryTextAxis(voucher);

  // Pas d'axe textuel : utiliser les données du type
  if (!primaryTextAxis) {
    return {
      description: typeDescription,
      conditions: typeConditions,
      isCustomized: false,
    };
  }

  const selectedValueKey = selectedCombination ? selectedCombination[primaryTextAxis.key] : null;

  // Pas de valeur sélectionnée : utiliser les données générales du type
  if (!selectedValueKey) {
    return {
      description: typeDescription,
      conditions: typeConditions,
      isCustomized: false,
    };
  }

  // Valeur sélectionnée : utiliser ses données spécifiques
  const selectedValueObj = primaryTextAxis.values?.find((v) => v.value === selectedValueKey);

  if (!selectedValueObj) {
    return {
      description: typeDescription,
      conditions: typeConditions,
      isCustomized: false,
    };
  }

  const variantDesc = selectedValueObj.translations?.variantDescription?.[language] || selectedValueObj.variantDescription || '';
  const variantCond = selectedValueObj.translations?.variantConditions?.[language] || selectedValueObj.variantConditions || '';

  return {
    description: variantDesc || typeDescription,
    conditions: variantCond || typeConditions,
    isCustomized: !!(variantDesc || variantCond),
  };
};

export default {
  getDisplayData,
  getContentData,
  getPrimaryTextAxis,
};
